import { NavigationProp, ParamListBase, useIsFocused, useNavigation } from "@react-navigation/native";
import React from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";

import { tBrandRepConnectionResponse, tVisualConnectionStatus } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import { useAppDispatch } from "src/redux";
import * as utilsDb from "src/db/utils.db";
import * as connectionDb from "src/db/connection.db";
import { PdBadge, PdBadgeLabel, PdStack, PdText } from "src/components/layout";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/layout/table";
import { useDebounce, useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import { kRoute } from "src/kRoute";
import { useSelector } from "react-redux";
import { tPressableState } from "src/components/types";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdMessage, PdUserAdd } from "src/components/Icons";
import { kTablePageSize } from "src/util/constants";
import { useInviteBrandsModal } from "src/hooks/modals";
import { PdIconButton } from "src/components/forms/icon-button";
import { setCurrentThread } from "src/redux/messages/actions";
import {
  changeFilterConnectionStatus,
  changeFilterNextPage,
  changeFilterPrevPage,
  changeFilterTerm,
  resetFilters,
} from "src/redux/filters/actions";
import { Carousel } from "src/components/ui";
import { formatDate, formatMoney } from "src/util/gFormatting";
import { formatDatabaseConnectionStatus } from "tmt_api/src/util/formatters";
import { flatMap } from "lodash";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { PdSearchInput } from "src/components/v2/ui";
import { PdFormLabel } from "src/components/v2/forms/control";
import { PdTab, PdTabLabel } from "src/components/v2/layout";

type tTableState = {
  data: tBrandRepConnectionResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const getThemeColorFromConnectionStatus = (status: tVisualConnectionStatus): string => {
  if (status === "Declined") return kColors.red200;
  if (status === "Pending") return kColors.yellow200;
  if (status === "Invited") return kColors.orange200;
  if (status === "Approved") return kColors.green200;
  if (status === "Active") return kColors.green200;
  return kColors.gray300;
};

const useGridLayout = () => {
  const { width } = useWindowDimensions();
  let numColumns = 5;
  if (width >= 1600) numColumns = 4;
  if (width >= 1200 && width < 1600) numColumns = 3;
  if (width >= 600 && width < 1200) numColumns = 2;
  if (width < 600) numColumns = 1;
  return numColumns;
};

const GAP = 30;

const BrandCard: React.FC<{ item: tBrandRepConnectionResponse; style?: StyleProp<ViewStyle> }> = ({ item, style }) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const rMessagesThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);

  const handleOnPressShopBrand = () => {
    navigation.navigate(kRoute.P100, { brand: item.brandName });
  };

  const handleOnPressMessage = () => {
    try {
      const rThreadId = rMessagesThreads.find((thread) => thread.recipient.includes(item.brandId));
      if (!rThreadId) throw new Error("An error occurred while attempting to parse the recipient's id.");
      dispatch(setCurrentThread(rThreadId));
      navigation.navigate(kRoute.P04);
    } catch (er) {
      dispatch({
        type: "RIBBON",
        message: new Error("Please use the left menu to send a message to the Retailer"),
      });
    }
  };

  return (
    <Pressable style={style}>
      {({ hovered }: tPressableState) => {
        return (
          <View style={hovered ? styles.hoveredBrandCard : styles.brandCard}>
            <TouchableOpacity onPress={handleOnPressShopBrand}>
              <Carousel
                style={styles.brandCarousel}
                mobileStyle={styles.brandImage}
                webStyle={webStyles.brandImage}
                imageUrls={item.brandImages}
              />
            </TouchableOpacity>
            <View style={styles.brandCardContent}>
              <View style={styles.brandCardTitleContainer}>
                <PdText style={styles.brandCardTitle}>{item.brandName}</PdText>
                <PdIconButton
                  onPress={handleOnPressMessage}
                  size="sm"
                  colorScheme="pink"
                  style={styles.brandCardMessageButton}
                  contentContainerStyle={styles.brandCardMessageButtonContent}
                  icon={<PdMessage style={styles.brandCardMessageButtonIcon} />}
                />
              </View>

              <PdText style={styles.brandCardSubtitle}>{`Opening Min. ${formatMoney(
                item.brandOpeningMinimum,
              )}`}</PdText>
              <PdText style={styles.brandCardSubtitleEnd}>{`Reorder Min. ${formatMoney(
                item.brandReorderMinimum,
              )}`}</PdText>
              <PdButton onPress={handleOnPressShopBrand} style={styles.brandCardShopButton} variant="outline">
                <PdButtonLabel>Shop Brand</PdButtonLabel>
              </PdButton>
            </View>
          </View>
        );
      }}
    </Pressable>
  );
};

const getStatusFilterValue = (value?: tVisualConnectionStatus | tVisualConnectionStatus[]) => {
  if (!value) return undefined;
  return Array.isArray(value)
    ? flatMap(value.map((v) => formatDatabaseConnectionStatus(v)))
    : formatDatabaseConnectionStatus(value);
};

const Row: React.FC<{ item: tRowInstance<tBrandRepConnectionResponse> }> = ({ item }) => {
  return (
    <Pressable>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow style={[hovered ? styles.tableRowHovered : {}, pressed ? styles.tableRowPressed : {}]}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};

const MobileRow: React.FC<{ item: tRowInstance<tBrandRepConnectionResponse> }> = ({ item }) => {
  return (
    <TouchableOpacity style={[styles.flexRow, styles.alignCenter, styles.py3]}>
      <View style={styles.flex1}>
        <PdText>{item.repName}</PdText>
        <PdText>{item.repEmail}</PdText>
      </View>
      <PdBadge style={[styles.ml2, { backgroundColor: getThemeColorFromConnectionStatus(item.status) }]}>
        <PdBadgeLabel>{item.status}</PdBadgeLabel>
      </PdBadge>
    </TouchableOpacity>
  );
};

const InvitedBrandsTable = ({ data }: { data: tBrandRepConnectionResponse[] }) => {
  const isMobile = useIsMobile();
  const tableInstance = useTable<tBrandRepConnectionResponse>({
    data,
    columns,
    initialState: {
      sortedBy: "createdAtInMillis",
      sortDirection: "desc",
    },
  });

  const renderItem: React.FC<{ item: tRowInstance<tBrandRepConnectionResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <MobileRow item={item} />;
      return <Row item={item} />;
    },
    [isMobile],
  );

  return (
    <PdTable
      ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
      data={tableInstance.rows}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      contentContainerStyle={styles.wFull}
    />
  );
};

const P17_RepMyBrands: React.FC<{ navigation: NavigationProp<ParamListBase> }> = () => {
  const numColumns = useGridLayout();
  const isFocused = useIsFocused();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const inviteBrandsModal = useInviteBrandsModal();
  const filters = useSelector((state) => state.FiltersReducer.repMyBrands);
  const dTermFilter = useDebounce(filters.term, 500);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });

  const handleOnPressInvite = async () => {
    inviteBrandsModal.openAsync({ companyId: rCompanyId });
  };

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "repMyBrands", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "repMyBrands", value: tableState?.next ?? "" }));
  };

  const renderItem: React.FC<{ item: tBrandRepConnectionResponse }> = React.useCallback(
    ({ item }) => {
      return <BrandCard item={item} style={{ flex: 1 / numColumns, margin: GAP / 2 }} />;
    },
    [numColumns],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const query = await utilsDb.withPagination(
        connectionDb.queryBrandRepConnection,
        connectionDb.documentBrandRepConnection,
        {
          term: dTermFilter,
          status: getStatusFilterValue(filters.connectionStatus),
          repId: rCompanyId,
          prev: filters.prev,
          next: filters.next,
          limit: kTablePageSize,
        },
      );

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapBrandRepConnection(doc.data()));
        setTableState({
          isLoading: false,
          data,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [dTermFilter, filters.connectionStatus, filters.next, filters.prev, rCompanyId]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={isMobile ? styles.mobileContainer : styles.webContainer}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={styles.headerContainer}>
        <PdText variant="subheader" style={isMobile ? styles.mb4 : {}}>
          My Brands
        </PdText>
        <PdStack direction={isMobile ? "vertical" : "horizontal"}>
          <PdButton
            colorScheme="purple"
            leftIcon={<PdUserAdd color="black" style={styles.buttonIcon} />}
            onPress={handleOnPressInvite}
          >
            <PdButtonLabel>Invite your Brands</PdButtonLabel>
          </PdButton>
        </PdStack>
      </PdStack>
      <View style={styles.mb4}>
        <PdFormLabel>Search your brands</PdFormLabel>
        <PdSearchInput
          value={filters.term}
          onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "repMyBrands", value }))}
          placeholder="Search Customers"
        />
      </View>
      <View style={styles.filtersContainer}>
        <PdTab
          isActive={filters.connectionStatus === "Active"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "repMyBrands", value: "Active" }))}
        >
          <PdTabLabel>Active</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Pending"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "repMyBrands", value: "Pending" }))}
        >
          <PdTabLabel>Pending</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.connectionStatus === "Invited"}
          onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "repMyBrands", value: "Invited" }))}
        >
          <PdTabLabel>Invited</PdTabLabel>
        </PdTab>
      </View>
      {["Pending", "Invited"].includes(filters.connectionStatus || "") ? (
        <InvitedBrandsTable data={tableState.data} />
      ) : (
        <FlatList
          numColumns={numColumns}
          data={tableState.data}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          style={{ marginHorizontal: -(GAP / 2) }}
        />
      )}
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </ScrollView>
  );
};

export const P17 = React.memo(P17_RepMyBrands);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  brandCard: {
    borderRadius: kStyleConst.kSpacing,
    height: "100%",
  },
  brandCardContent: {
    flex: 1,
    padding: 4 * kStyleConst.kSpacing,
  },
  brandCardMessageButton: {
    borderRadius: 999,
    marginLeft: "auto",
  },
  brandCardMessageButtonContent: {
    borderRadius: 999,
  },
  brandCardMessageButtonIcon: {
    height: 15,
    width: 15,
  },
  brandCardShopButton: {
    marginRight: "auto",
    marginTop: "auto",
  },
  brandCardSubtitle: {
    fontSize: 12,
    lineHeight: 20,
  },
  brandCardSubtitleEnd: {
    fontSize: 12,
    lineHeight: 20,
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  brandCardTitle: {
    fontWeight: "500",
  },
  brandCardTitleContainer: {
    flexDirection: "row",
  },
  brandCarousel: {
    borderRadius: kStyleConst.kSpacing,
  },
  brandImage: {
    backgroundColor: kColors.gray100,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    height: 300,
  },
  buttonIcon: {
    height: 14,
    width: 14,
  },
  filtersContainer: {
    alignItems: "center",
    backgroundColor: kColors.white,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing * 2,
    width: "100%",
  },
  flex1: {
    flex: 1,
  },
  flex2: {
    flex: 2,
  },
  flexRow: {
    flexDirection: "row",
  },
  headerContainer: {
    justifyContent: "space-between",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  hoveredBrandCard: {
    backgroundColor: kColors.gray50,
    borderRadius: kStyleConst.kSpacing,
    height: "100%",
  },
  loadingContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mobileContainer: {
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  py3: {
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  tableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  tableRowPressed: {
    opacity: 0.6,
  },
  wFull: {
    width: "100%",
  },
  webContainer: {
    marginBottom: kStyleConst.kSpacing * 8,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  brandImage: {
    backgroundColor: kColors.gray100,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    height: 300,
  },
} as const;

const columns: tColumn<tBrandRepConnectionResponse>[] = [
  {
    id: "connectionDate",
    header: "Date",
    accessor: (originalRow) => formatDate(originalRow.createdAtInMillis),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "brandEmail",
    header: "Email",
    accessor: (originalRow) => originalRow.brandEmail || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex2,
    style: styles.flex2,
  },
  {
    id: "status",
    header: "Status",
    accessor: (originalRow) => originalRow.status,
    Cell: ({ value, row }) => (
      <PdBadge style={{ backgroundColor: getThemeColorFromConnectionStatus(row.status) }}>
        <PdBadgeLabel>{value}</PdBadgeLabel>
      </PdBadge>
    ),
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
];
